import { type OpenApiSchemaMapping } from '#shared/api/schema';
import { INTEGRATION_MAPPING_STATIC_ITEMS } from '#shared/lib/integrations';
import {
    type IntegrationMappingCharacteristic,
    type IntegrationMappingEditorOption,
    type IntegrationMappingEditorParameter,
    type IntegrationMappingEditorPropertyParameter,
    type IntegrationMappingEditorRow,
} from './types';

export const INTEGRATION_MAPPING_EXTERNAL_NAME_MAX_LENGTH = 10_000;

const getParameterKey = (type: IntegrationMappingEditorParameter['type'], id: string) => `${type}:${id}`;

const PROPERTY_PARAMETERS: IntegrationMappingEditorPropertyParameter[] = INTEGRATION_MAPPING_STATIC_ITEMS.map(
    ({ value, content }) => {
        return {
            key: getParameterKey('property', value),
            id: value,
            title: content,
            type: 'property',
        };
    }
);

const getCharacteristicParameter = ({
    id,
    title,
}: IntegrationMappingCharacteristic): IntegrationMappingEditorOption => {
    return {
        key: getParameterKey('characteristic', id),
        id,
        title,
        type: 'characteristic',
    };
};

export const buildIntegrationMappingEditorRows = (
    mapping: OpenApiSchemaMapping,
    characteristics: IntegrationMappingCharacteristic[]
): IntegrationMappingEditorRow[] => {
    const characteristicById = new Map(characteristics.map((characteristic) => [characteristic.id, characteristic]));

    const propertyRows = mapping.custom_slugs.flatMap((slug) => {
        const parameter = PROPERTY_PARAMETERS.find(({ id }) => id === slug);

        return parameter ? [{ ...parameter, externalName: parameter.title }] : [];
    });

    const characteristicRows = mapping.characteristic_ids.map((id) => {
        const characteristic = characteristicById.get(id);
        const title = characteristic?.title ?? id;
        const externalMapping = mapping.characteristic_mappings?.[id];

        return {
            ...getCharacteristicParameter({ id, title }),
            externalName: externalMapping?.external_name ?? externalMapping?.external_id ?? title,
        };
    });

    return [...propertyRows, ...characteristicRows];
};

export const buildIntegrationMappingEditorOptions = (
    mapping: OpenApiSchemaMapping,
    characteristics: IntegrationMappingCharacteristic[]
): IntegrationMappingEditorOption[] => {
    const selectedPropertyIds = new Set(mapping.custom_slugs);
    const selectedCharacteristicIds = new Set(mapping.characteristic_ids);

    return [
        ...PROPERTY_PARAMETERS.filter(({ id }) => !selectedPropertyIds.has(id)),
        ...characteristics.filter(({ id }) => !selectedCharacteristicIds.has(id)).map(getCharacteristicParameter),
    ];
};

export const addIntegrationMappingParameter = (
    mapping: OpenApiSchemaMapping,
    parameter: IntegrationMappingEditorOption
): OpenApiSchemaMapping => {
    if (parameter.type === 'property') {
        if (mapping.custom_slugs.includes(parameter.id)) {
            return mapping;
        }

        return {
            ...mapping,
            custom_slugs: [...mapping.custom_slugs, parameter.id],
        };
    }

    if (mapping.characteristic_ids.includes(parameter.id)) {
        return mapping;
    }

    return {
        ...mapping,
        characteristic_ids: [...mapping.characteristic_ids, parameter.id],
    };
};

export const removeIntegrationMappingParameter = (
    mapping: OpenApiSchemaMapping,
    parameter: IntegrationMappingEditorParameter
): OpenApiSchemaMapping => {
    if (parameter.type === 'property') {
        return {
            ...mapping,
            custom_slugs: mapping.custom_slugs.filter((slug) => slug !== parameter.id),
        };
    }

    if (!mapping.characteristic_mappings) {
        return {
            ...mapping,
            characteristic_ids: mapping.characteristic_ids.filter((id) => id !== parameter.id),
        };
    }

    const characteristicMappings = { ...mapping.characteristic_mappings };

    delete characteristicMappings[parameter.id];

    return {
        ...mapping,
        characteristic_ids: mapping.characteristic_ids.filter((id) => id !== parameter.id),
        characteristic_mappings: characteristicMappings,
    };
};

export const changeIntegrationMappingExternalName = (
    mapping: OpenApiSchemaMapping,
    characteristicId: string,
    externalName: string
): OpenApiSchemaMapping => {
    return {
        ...mapping,
        characteristic_mappings: {
            ...mapping.characteristic_mappings,
            [characteristicId]: { external_name: externalName },
        },
    };
};

export const isIntegrationMappingExternalNameValid = (externalName: string) =>
    externalName.trim().length > 0 && externalName.length <= INTEGRATION_MAPPING_EXTERNAL_NAME_MAX_LENGTH;
